import type { DscApplicantSection } from "@credit-acceptance/step6-bff-client";
import type { ReactNode, SyntheticEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../components/ui/form";
import type { Tier } from "../../../../components/ui/tierBadge";
import { useArmStipulationCategoryFilter } from "../../../../hooks/useArmStipulationCategoryFilter";
import { useScrollSpy } from "../../../../hooks/useScrollSpy";
import { roundToDecimals } from "../../../../lib/money";
import { getErrorMessage } from "../../../../lib/query/errors";
import { cn } from "../../../../lib/utils";
import { useIncomeChangeStore } from "../../../../stores/incomeChangeStore";
import { useReadOnly } from "../../../lock/ReadOnlyContext";
import type { ScrollSpySection } from "../../../stipulations/stipulationCategoryFilters";
import { ALL_STIPULATION_CATEGORIES } from "../../../stipulations/stipulationCategoryFilters";
import { useStipulationCategoryStore } from "../../../stipulations/stipulationCategoryStore";
import Section from "../../components/Section";
import { useRegisterSaveableSection } from "../../useRegisterSaveableSection";
import { isBankNameOptionalForTier, MAX_INCOMES_PER_PERSON } from "./constants";
import type { SignerColumnSections } from "./fields/useSignerColumnSections";
import { useSignerColumnSections } from "./fields/useSignerColumnSections";
import { ReadOnlyProvider } from "./readOnlyContext";
import { buildSignerApplicantPutRequest } from "./Signers.putMapping";
import { useSignersForm } from "./useSignersForm";

// Row-start Tailwind classes, looked up by number — Tailwind's JIT compiler
// needs static literal classes, so `xl:row-start-${n}` can't be interpolated.
// Must cover through HIGHEST_ROW below (header + banners + one row per income
// block up to MAX_INCOMES_PER_PERSON + total/bank/customer) — add a line here
// (and bump the literal `xl:row-start-N` class) if either grows.
const ROW_START_CLASSES: Record<number, string> = {
  1: "xl:row-start-1",
  2: "xl:row-start-2",
  3: "xl:row-start-3",
  4: "xl:row-start-4",
  5: "xl:row-start-5",
  6: "xl:row-start-6",
  7: "xl:row-start-7",
  8: "xl:row-start-8",
};

// Fixed rows shared by both signer columns; INCOME_ROW_START through the
// total/bank/customer rows are sized off whichever signer has more income
// blocks (see SignerGridColumn's maxIncomeCount prop) so a row a signer
// doesn't have just renders blank on their side instead of shifting the rows
// beneath it.
const HEADER_ROW = 1;
const BANNERS_ROW = 2;
const INCOME_ROW_START = 3;

// header(1) + banners(1) + one row per income block + total/bank/customer(3)
const HIGHEST_ROW = 2 + MAX_INCOMES_PER_PERSON + 3;

// Literal counterparts of HIGHEST_ROW (8) for the outer grid's row template
// and each SignerGridColumn's row-span — also can't be interpolated (same JIT
// constraint as ROW_START_CLASSES). Bump both alongside ROW_START_CLASSES.
const OUTER_GRID_ROWS_CLASS = "xl:grid-rows-[repeat(8,auto)]";
const ROW_SPAN_CLASS = "xl:row-span-8";

if (import.meta.env.DEV && !(HIGHEST_ROW in ROW_START_CLASSES)) {
  throw new Error(
    `ROW_START_CLASSES only covers rows 1-${Object.keys(ROW_START_CLASSES).length}, but MAX_INCOMES_PER_PERSON=${MAX_INCOMES_PER_PERSON} needs it to cover through row ${HIGHEST_ROW} — add the missing entries (and update OUTER_GRID_ROWS_CLASS / ROW_SPAN_CLASS's literal row count) in Signers.tsx.`
  );
}

interface SignerGridCellProperties {
  row: number;
  className?: string;
  children: ReactNode;
  /** Set only for cells whose content actually reads `@sm`/`@lg`/`@xl` container-query classes (income blocks, Customer Information) — see SignerGridColumn. */
  container?: boolean;
}

/**
 * Places one piece of a signer's content (header, banners, an income block,
 * ...) at an explicit row within its SignerGridColumn's subgrid — only at
 * `xl` and up. Below `xl` the two signers stack, so pieces just flow in DOM
 * order instead. `container` opts this cell into `@container`, carrying
 * forward the same container-query context IncomeBlock/CustomerInformation
 * size their own field rows against (this cell is the box with the column's
 * actual rendered width) — only set where that's actually needed, not on
 * every cell.
 */
function SignerGridCell({ row, className, children, container }: SignerGridCellProperties) {
  return <div className={cn("min-w-0", container && "@container", ROW_START_CLASSES[row], className)}>{children}</div>;
}

interface SignerGridColumnProperties {
  column: 1 | 2;
  /** Accessible region name for this signer's whole content (Driver / 2nd Signer). */
  label: string;
  sections: SignerColumnSections;
  /** Shared across both signers — see the comment on Signers()'s maxIncomeCount. */
  maxIncomeCount: number;
  /** Extra classes for the header cell only (e.g. the stacked-layout divider above 2nd Signer). */
  headerClassName?: string;
}

/**
 * One signer's full column of grid cells (header, banners, income blocks,
 * total row, Bank/Customer Information). Driver and 2nd Signer each render
 * one of these as a real, independently-boxed `<section>` — placed in
 * `column`, spanning every row via `xl:grid-rows-subgrid` — so their cells
 * share the outer grid's row *tracks* (same numbers as the sibling section)
 * without needing a `display:contents` wrapper to expose them as direct grid
 * items. A row a signer doesn't have (fewer income blocks, no banners) just
 * leaves that shared row blank on their side instead of shifting rows below.
 */
function SignerGridColumn({ column, label, sections, maxIncomeCount, headerClassName }: SignerGridColumnProperties) {
  const totalRow = INCOME_ROW_START + maxIncomeCount;
  const bankRow = totalRow + 1;
  const customerRow = bankRow + 1;

  return (
    <section
      aria-label={label}
      className={cn(
        // Below xl, subgrid isn't active — the inner grid stacks Header/Income/
        // Bank/Residence with its own row tracks, so it needs its own gap-y-3
        // (rather than the outer grid's) to avoid these subsections touching.
        // At xl+, this matches the outer grid's gap-y-3 exactly, so subgrid's
        // shared row-gap looks the same either way.
        "grid grid-cols-1 gap-y-3 xl:grid-rows-subgrid",
        column === 1 ? "xl:col-start-1" : "xl:col-start-2",
        ROW_SPAN_CLASS
      )}
    >
      <SignerGridCell row={HEADER_ROW} className={headerClassName}>
        {sections.header}
      </SignerGridCell>
      {sections.banners && <SignerGridCell row={BANNERS_ROW}>{sections.banners}</SignerGridCell>}
      {sections.incomeBlocks.map((block, index) => (
        <SignerGridCell key={block.key} row={INCOME_ROW_START + index} container>
          {block}
        </SignerGridCell>
      ))}
      <SignerGridCell row={totalRow}>{sections.totalRow}</SignerGridCell>
      <SignerGridCell row={bankRow}>{sections.bankInformation}</SignerGridCell>
      <SignerGridCell row={customerRow} container>
        {sections.customerInformation}
      </SignerGridCell>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Signers card — collapsible outer card (via Section/Accordion) with a
// two-column Driver | 2nd Signer layout on desktop, stacked on mobile.
//
// Visibility is fully driven by the DSC Workbench dashboard response: the
// Driver column renders only when `driver.present` is true, and the
// 2nd Signer column only when `secondSigner.present` is true. There is
// no add/remove-2nd-signer flow — the response is the single source of truth
// for who appears on the deal.
//
// Income blocks are hydrated from each applicant's income.employments on
// mount (see useSignersForm/incomeMapping); an applicant with no employments
// seeds exactly ONE blank income block.
// ---------------------------------------------------------------------------
// Splits a camelCase field name into words for a human-readable label
// (e.g. "employerName" -> "Employer Name"), so validation-failure banners
// name the actual offending field instead of guessing.
function humanizeFieldName(name: string): string {
  const spaced = name.replaceAll(/([\da-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Walks a react-hook-form FieldErrors tree and collects the (deduped, human-
// readable) names of every leaf field that failed validation. A node is a
// leaf when it carries RHF's own `type`/`message` (its nested-object shape is
// otherwise indistinguishable from a plain sub-schema object).
function collectInvalidFieldNames(node: unknown, results: Set<string> = new Set()): Set<string> {
  if (node == null || typeof node !== "object") {
    return results;
  }
  const record = node as Record<string, unknown>;
  if (typeof record.type === "string" && typeof record.message === "string") {
    return results;
  }
  for (const [key, value] of Object.entries(record)) {
    if (key === "ref") {
      continue;
    }
    if (value && typeof value === "object") {
      const child = value as Record<string, unknown>;
      if (typeof child.type === "string" && typeof child.message === "string") {
        results.add(humanizeFieldName(key));
      } else {
        collectInvalidFieldNames(value, results);
      }
    }
  }
  return results;
}

interface SignersProperties {
  /** Primary applicant (driver) section from the dashboard response. */
  driver?: DscApplicantSection;
  /** Second signer (cosigner) section from the dashboard response. */
  secondSigner?: DscApplicantSection;
  /** Deal / approval id, threaded down to each income block's employer-info flow. */
  approvalId?: string;
  /** Deal scoring-program tier — Bank Name stays optional for GOLD/PLATINUM (OR-23394). */
  tier?: Tier;
  /** True when the deal is a pre-approval app — Bank Name stays optional (OR-23394 follow-up). */
  isPreApprovalApp?: boolean;
}

function Signers({ driver, secondSigner, approvalId, tier, isPreApprovalApp = false }: SignersProperties) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const showDriver = Boolean(driver?.present);
  const showSecondSigner = Boolean(secondSigner?.present);

  const isBankNameOptional = isBankNameOptionalForTier(tier, isPreApprovalApp);
  const { form, control, driverIncomes, secondSignerIncomes, driverSsnType, secondSignerSsnType } = useSignersForm({
    driver,
    secondSigner,
    isBankNameOptional,
  });

  // Income count as of the last hydration/save — the baseline `deleteSlots`
  // is diffed against at save time (see buildSignerApplicantPutRequest).
  // Re-baselined in onSaved below so a later remove-then-save cycle diffs
  // against the just-saved count, not the form's initial mount.
  const [originalDriverIncomeCount, setOriginalDriverIncomeCount] = useState(driverIncomes.fields.length);
  const [originalSecondSignerIncomeCount, setOriginalSecondSignerIncomeCount] = useState(
    secondSignerIncomes.fields.length
  );

  // getPayload snapshots the counts actually sent on the request; onSaved
  // re-baselines from this snapshot rather than the live field-array length,
  // since further edits can land while the save request is in flight.
  const sentIncomeCountsReference = useRef({
    driver: originalDriverIncomeCount,
    secondSigner: originalSecondSignerIncomeCount,
  });

  // formState.errors returned by useForm() is only refreshed on render (it's
  // React state gated behind RHF's read-tracking proxy), so it can't be read
  // reliably from an async callback right after `trigger()` resolves. Reading
  // it here, in render, via `useFormState` keeps the banners live-derived
  // from RHF's internal state instead of a stale closure snapshot.
  const { errors } = useFormState({ control });
  const driverValidationError = useMemo(
    () =>
      errors.driver
        ? t("signers.validationError", { fields: [...collectInvalidFieldNames(errors.driver)].join(", ") })
        : undefined,
    [errors.driver, t]
  );
  const secondSignerValidationError = useMemo(
    () =>
      errors.secondSigner
        ? t("signers.validationError", { fields: [...collectInvalidFieldNames(errors.secondSigner)].join(", ") })
        : undefined,
    [errors.secondSigner, t]
  );

  // Save-time errors (server warnings / save failures) are distinct from live
  // validation errors above and only cleared at the start of the next save.
  const [driverSaveError, setDriverSaveError] = useState<string>();
  const [secondSignerSaveError, setSecondSignerSaveError] = useState<string>();
  const driverError = driverValidationError ?? driverSaveError;
  const secondSignerError = secondSignerValidationError ?? secondSignerSaveError;

  // Pushes live per-row gross-income amounts (keyed by each row's stable
  // field-array id) to incomeChangeStore so the (form-tree-external) Save &
  // Exit modal can warn when an edit hasn't been saved yet — see
  // incomeChangeStore.ts for why this needs a store bridge, and why it's
  // keyed by row id rather than a combined total: a deleted row must never
  // read as "the income changed," only an edit to a still-present row should.
  // Watches only the monthlyGrossPayAmount leaf per income row (not the whole
  // driver.incomes/secondSigner.incomes arrays) so edits to unrelated income
  // fields (employer name, address, etc.) don't re-run this.
  const watchedDriverGrossPay = useWatch({
    control,
    name: driverIncomes.fields.map((_, index) => `driver.incomes.${index}.monthlyGrossPayAmount` as const),
  });
  const watchedSecondSignerGrossPay = useWatch({
    control,
    name: secondSignerIncomes.fields.map((_, index) => `secondSigner.incomes.${index}.monthlyGrossPayAmount` as const),
  });
  const setDriverCurrent = useIncomeChangeStore((state) => state.setDriverCurrent);
  const setSecondSignerCurrent = useIncomeChangeStore((state) => state.setSecondSignerCurrent);
  const commitDriverBaseline = useIncomeChangeStore((state) => state.commitDriverBaseline);
  const commitSecondSignerBaseline = useIncomeChangeStore((state) => state.commitSecondSignerBaseline);
  const resetIncomeChangeStore = useIncomeChangeStore((state) => state.reset);

  // Each deal remount (key={approvalId} in ReviewPage) must start with a
  // clean baseline — incomeChangeStore lives outside the RHF instance, so it
  // doesn't get reset by the remount on its own.
  useEffect(() => {
    resetIncomeChangeStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per mount only
  }, []);

  useEffect(() => {
    const amountsById = Object.fromEntries(
      driverIncomes.fields.map((field, index) => [field.id, roundToDecimals(watchedDriverGrossPay[index] ?? 0)])
    );
    setDriverCurrent(amountsById);
  }, [watchedDriverGrossPay, driverIncomes.fields, setDriverCurrent]);

  useEffect(() => {
    const amountsById = Object.fromEntries(
      secondSignerIncomes.fields.map((field, index) => [
        field.id,
        roundToDecimals(watchedSecondSignerGrossPay[index] ?? 0),
      ])
    );
    setSecondSignerCurrent(amountsById);
  }, [watchedSecondSignerGrossPay, secondSignerIncomes.fields, setSecondSignerCurrent]);

  // Runs the Zod validation; the error banners above react to the resulting
  // formState.errors automatically. Shared by the orchestrator (real Save
  // button / Save & Exit) and the native <form onSubmit> below.
  const validate = async (): Promise<boolean> => {
    setDriverSaveError(undefined);
    setSecondSignerSaveError(undefined);
    return form.trigger();
  };

  useRegisterSaveableSection("signers", {
    validate,
    getPayload: () => {
      const values = form.getValues();
      sentIncomeCountsReference.current = {
        driver: values.driver.incomes.length,
        secondSigner: values.secondSigner?.incomes.length ?? originalSecondSignerIncomeCount,
      };
      return {
        signerApplicant: buildSignerApplicantPutRequest(values.driver, isBankNameOptional, originalDriverIncomeCount),
        secondSignerApplicant:
          showSecondSigner && values.secondSigner
            ? buildSignerApplicantPutRequest(values.secondSigner, isBankNameOptional, originalSecondSignerIncomeCount)
            : undefined,
      };
    },
    onSaved: () => {
      // Per-applicant save-time error banners are intentionally not derived
      // from the save response's `metadata.warnings` array for now.
      commitDriverBaseline();
      commitSecondSignerBaseline();
      setOriginalDriverIncomeCount(sentIncomeCountsReference.current.driver);
      setOriginalSecondSignerIncomeCount(sentIncomeCountsReference.current.secondSigner);
      // Re-baselines RHF's own dirty tracking to the just-persisted values
      // (`keepValues` leaves the displayed values and field-array ids
      // untouched — only `isDirty`/`dirtyFields` are cleared). Without this,
      // `isDirty` stays true for the rest of the session after any edit,
      // which permanently blocks useSignersForm's dashboard-resync effect
      // (e.g. after a Call Script save) even though nothing is unsaved.
      form.reset(undefined, { keepValues: true, keepDirty: false, keepErrors: true });
    },
    onSaveFailed: (error) => {
      setDriverSaveError(t("signers.saveError", { message: getErrorMessage(error) }));
    },
  });

  // The real Save button (and Save & Exit) run validate()+getPayload() through
  // the shared save orchestrator (see useSaveAllSections), not this handler.
  // This wrapper only runs validation for a native form submit (Enter key) so
  // the same banners still appear — it never makes its own save call.
  const onSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    void validate();
  };

  // Real applicant ids for the Work Number flow, sourced straight from the
  // dashboard response (driver/secondSigner.applicantId).
  const driverApplicantId = driver?.applicantId;
  const secondSignerApplicantId = secondSigner?.applicantId;

  // Scroll-spy: as the analyst scrolls past each Income / Bank / Residence
  // subsection (in either column), drive the Stipulations panel's category
  // filter to match. Called unconditionally — before the early return below —
  // per the rules of hooks. `setCategoryFromScroll` only takes effect once
  // `useArmStipulationCategoryFilter` below has armed the filter (real scroll
  // or form input) — until then the panel stays on "All Stips".
  const setStipulationCategoryFromScroll = useStipulationCategoryStore((state) => state.setCategoryFromScroll);
  // `useScrollSpy` reports `undefined` once the analyst scrolls back above the
  // first section (no section left in the activation band) — treat that the
  // same as "All Stips" rather than leaving the filter stuck on whichever
  // section was last active. When both signer columns' matching section are
  // intersecting at once (side by side layout), scope to "both"; otherwise
  // scope to whichever single column is active.
  const { registerSection, resyncActiveSection } = useScrollSpy<ScrollSpySection>((active) => {
    if (!active) {
      setStipulationCategoryFromScroll(ALL_STIPULATION_CATEGORIES, "both");
      return;
    }
    setStipulationCategoryFromScroll(active.category, active.applicants.length === 2 ? "both" : active.applicants[0]);
  });
  // A ref-callback (not a plain useRef) so its listeners attach to the real
  // <form> node as soon as it mounts, even on a later render than this hook's
  // first call (e.g. if the early-return guard below ever starts returning
  // null on an initial render before flipping to the form).
  const registerFormForArming = useArmStipulationCategoryFilter();

  // While pinned at the top of the page, the store forces the category to
  // "All Stips" and ignores the scroll-spy's reports (see `atTop` in
  // `stipulationCategoryStore`). If the section that was active before
  // reaching the top is STILL the active one once the analyst scrolls back
  // down, its IntersectionObserver entry never toggles again — nothing would
  // otherwise re-apply it. Resync as soon as the pin releases so the current
  // section (even if unchanged) is re-delivered.
  const atTop = useStipulationCategoryStore((state) => state.atTop);
  useEffect(() => {
    if (!atTop) {
      resyncActiveSection();
    }
  }, [atTop, resyncActiveSection]);

  // Split each signer's content into independent pieces (header, banners, one
  // per income block, ...) so matching pieces from both signers can share a
  // grid row below and stay vertically aligned — see useSignerColumnSections
  // and SignerGridCell. Called unconditionally (rules of hooks) even when a
  // signer isn't shown; the underlying field arrays already exist regardless.
  const driverSections = useSignerColumnSections({
    title: t("signers.driver"),
    personPath: "driver",
    control,
    fields: driverIncomes.fields,
    append: driverIncomes.append,
    remove: driverIncomes.remove,
    approvalId,
    applicantId: driverApplicantId,
    applicantSsnType: driverSsnType,
    registerSection,
    errorMessage: driverError,
  });
  const secondSignerSections = useSignerColumnSections({
    title: t("signers.secondSigner"),
    personPath: "secondSigner",
    control,
    fields: secondSignerIncomes.fields,
    append: secondSignerIncomes.append,
    remove: secondSignerIncomes.remove,
    approvalId,
    applicantId: secondSignerApplicantId,
    applicantSsnType: secondSignerSsnType,
    registerSection,
    errorMessage: secondSignerError,
  });

  // Shared between both signers' SignerGridColumn so an income row either
  // signer doesn't have just renders blank on their side instead of shifting
  // the rows beneath it.
  const maxIncomeCount = Math.max(driverSections.incomeBlocks.length, secondSignerSections.incomeBlocks.length);

  // Nothing to render until the response includes at least one present signer.
  if (!showDriver && !showSecondSigner) {
    return null;
  }

  return (
    <Section title={t("signers.sectionTitle")} hasError={Boolean(errors.driver) || Boolean(errors.secondSigner)}>
      <Form {...form}>
        <form ref={registerFormForArming} onSubmit={onSubmit} noValidate>
          {/* A disabled fieldset disables every nested *native* control, so the
              whole Signers form goes read-only under the VLA lock. Radix Select
              triggers and the tel: call anchor ignore the fieldset, so they read
              the same flag from ReadOnlyProvider instead. */}
          <ReadOnlyProvider value={readOnly}>
            <fieldset disabled={readOnly} className="m-0 w-full min-w-0 border-0 p-0">
              {/* Two-column layout: Driver | 2nd Signer. Below xl, each
                  signer's pieces flow in DOM order (stacked); at xl+, pieces
                  from both signers are explicitly placed so matching pieces
                  share a row (see SignerGridColumn / SignerGridCell above). */}
              <div className={cn("relative grid grid-cols-1 gap-x-6 gap-y-3 xl:grid-cols-2", OUTER_GRID_ROWS_CLASS)}>
                {showDriver && (
                  <SignerGridColumn
                    column={1}
                    label={t("signers.driver")}
                    sections={driverSections}
                    maxIncomeCount={maxIncomeCount}
                  />
                )}

                {showSecondSigner && (
                  // When both columns render, the 2nd Signer's header carries a
                  // divider: horizontal above it when stacked (below xl — the
                  // vertical divider below is xl-only), none when it stands alone.
                  <SignerGridColumn
                    column={2}
                    label={t("signers.secondSigner")}
                    sections={secondSignerSections}
                    maxIncomeCount={maxIncomeCount}
                    headerClassName={showDriver ? "border-border border-t pt-6 xl:border-t-0 xl:pt-0" : undefined}
                  />
                )}

                {/* Vertical divider between columns at xl+ — only when both signers render. */}
                {showDriver && showSecondSigner && (
                  <div
                    aria-hidden="true"
                    className="border-border absolute inset-y-0 left-1/2 hidden -translate-x-1/2 border-l xl:block"
                  />
                )}
              </div>
            </fieldset>
          </ReadOnlyProvider>
        </form>
      </Form>
    </Section>
  );
}

export default Signers;
